import { useState } from 'react';
import { Search, Plus, Package, Eye, EyeOff, Pencil } from 'lucide-react';
import {
  useInsumos,
  useCreateInsumo,
  useUpdateInsumo,
  useDeleteInsumo,
  type InsumoDTO,
  type CreateInsumoInput,
} from '../../hooks/useInsumos';
import { useAuthStore } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { SkeletonTable } from '../../components/ui/Skeleton';

const CATEGORIAS = ['PINTURA', 'IMPERMEABILIZACION', 'GYPSUM'] as const;
const PRESENTACIONES = ['UNIDAD', 'GALON', 'LITRO', 'KILO', 'CANECA'] as const;

type FilterCat = 'TODOS' | typeof CATEGORIAS[number];

const CATEGORIA_LABELS: Record<FilterCat, string> = {
  TODOS: 'Todos',
  PINTURA: 'Pintura',
  IMPERMEABILIZACION: 'Impermeabilización',
  GYPSUM: 'Gypsum',
};

const CATEGORIA_OPTIONS = CATEGORIAS.map((c) => ({ value: c, label: CATEGORIA_LABELS[c] }));
const PRESENTACION_OPTIONS = PRESENTACIONES.map((p) => ({ value: p, label: p.charAt(0) + p.slice(1).toLowerCase() }));

function getCategoriaColor(categoria: string): 'blue' | 'green' | 'orange' | 'gray' {
  switch (categoria) {
    case 'GYPSUM': return 'blue';
    case 'PINTURA': return 'green';
    case 'IMPERMEABILIZACION': return 'orange';
    default: return 'gray';
  }
}

export function InsumosTab() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const [filterCat, setFilterCat] = useState<FilterCat>('TODOS');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInsumo, setEditingInsumo] = useState<InsumoDTO | null>(null);

  const { data: allInsumos = [], isLoading, isError, refetch } = useInsumos();

  const filtered = allInsumos.filter((i) => {
    if (!showInactive && !i.isActive) return false;
    if (filterCat !== 'TODOS' && i.categoria !== filterCat) return false;
    if (search && !i.detalle.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const tabs: FilterCat[] = ['TODOS', ...CATEGORIAS];

  if (isLoading) {
    return <Card><div className="p-6"><SkeletonTable rows={5} cols={5} /></div></Card>;
  }

  if (isError) {
    return (
      <Card>
        <div className="px-6 py-12 text-center">
          <p className="text-sm text-gray-500">Error al cargar insumos</p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white/80 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? <Eye size={14} /> : <EyeOff size={14} />}
            {showInactive ? 'Ver activos' : 'Ver inactivos'}
          </button>
        </div>
        {canEdit && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
            Nuevo Insumo
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
          placeholder="Buscar insumo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Category filter */}
      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              filterCat === tab ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setFilterCat(tab)}
          >
            {CATEGORIA_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Content */}
      {filtered.length === 0 && !search ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Package size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">No hay insumos aún</h2>
            <p className="mt-1 text-sm text-gray-400">Agrega el primer insumo al catálogo.</p>
            {canEdit && (
              <Button variant="primary" className="mt-4" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
                Crear Insumo
              </Button>
            )}
          </div>
        </Card>
      ) : filtered.length === 0 && search ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Search size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">Sin resultados</h2>
            <p className="mt-1 text-sm text-gray-400">
              No encontramos insumos que coincidan con &quot;{search}&quot;
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden sm:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Detalle</th>
                    <th className="px-4 py-3">Categoría</th>
                    <th className="px-4 py-3">Presentación</th>
                    <th className="px-4 py-3">Precio Unit.</th>
                    {showInactive && <th className="px-4 py-3">Estado</th>}
                    {canEdit && <th className="px-4 py-3 text-right">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((insumo) => (
                    <InsumoRow
                      key={insumo.id}
                      insumo={insumo}
                      showInactive={showInactive}
                      canEdit={canEdit}
                      onEdit={canEdit ? () => setEditingInsumo(insumo) : undefined}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 sm:hidden">
            {filtered.map((insumo) => (
              <InsumoCard
                key={insumo.id}
                insumo={insumo}
                onClick={canEdit ? () => setEditingInsumo(insumo) : undefined}
              />
            ))}
          </div>
        </>
      )}

      {/* Mobile FAB */}
      {canEdit && (
        <button
          className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dd5113] text-white shadow-lg transition-transform hover:scale-105 sm:hidden"
          onClick={() => setShowCreateModal(true)}
          aria-label="Nuevo Insumo"
        >
          <Plus size={24} />
        </button>
      )}

      <CreateInsumoModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
      {editingInsumo && (
        <EditInsumoModal insumo={editingInsumo} onClose={() => setEditingInsumo(null)} />
      )}
    </div>
  );
}

function InsumoRow({
  insumo,
  showInactive,
  canEdit,
  onEdit,
}: {
  insumo: InsumoDTO;
  showInactive: boolean;
  canEdit: boolean;
  onEdit?: () => void;
}) {
  return (
    <tr
      className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${!insumo.isActive ? 'opacity-50' : ''}`}
    >
      <td className="px-4 py-3">
        <p className="font-medium text-gray-800">{insumo.detalle}</p>
      </td>
      <td className="px-4 py-3">
        <Badge color={getCategoriaColor(insumo.categoria)}>{CATEGORIA_LABELS[insumo.categoria]}</Badge>
      </td>
      <td className="px-4 py-3 text-gray-600 text-xs">{insumo.presentacion}</td>
      <td className="px-4 py-3 font-medium text-gray-800">${insumo.precioUnitario.toFixed(2)}</td>
      {showInactive && (
        <td className="px-4 py-3">
          <Badge color={insumo.isActive ? 'green' : 'gray'}>{insumo.isActive ? 'Activo' : 'Inactivo'}</Badge>
        </td>
      )}
      {canEdit && (
        <td className="px-4 py-3 text-right">
          <button
            onClick={onEdit}
            className="text-gray-400 hover:text-brand-blue transition-colors"
            title="Editar"
          >
            <Pencil size={16} />
          </button>
        </td>
      )}
    </tr>
  );
}

function InsumoCard({ insumo, onClick }: { insumo: InsumoDTO; onClick?: () => void }) {
  return (
    <div
      className={`glass-card p-4 ${!insumo.isActive ? 'opacity-50' : ''} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-gray-800">{insumo.detalle}</p>
          <p className="text-xs text-gray-400">{insumo.presentacion}</p>
        </div>
        <p className="font-semibold text-gray-800">${insumo.precioUnitario.toFixed(2)}</p>
      </div>
      <div className="mt-2 flex gap-1">
        <Badge color={getCategoriaColor(insumo.categoria)}>{CATEGORIA_LABELS[insumo.categoria]}</Badge>
      </div>
    </div>
  );
}

function CreateInsumoModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const createMutation = useCreateInsumo();
  const [form, setForm] = useState<CreateInsumoInput>({
    detalle: '',
    categoria: 'PINTURA',
    presentacion: 'UNIDAD',
    precioUnitario: 0,
  });

  const handleSubmit = async () => {
    if (!form.detalle.trim() || form.precioUnitario <= 0) return;
    await createMutation.mutateAsync(form);
    setForm({ detalle: '', categoria: 'PINTURA', presentacion: 'UNIDAD', precioUnitario: 0 });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} title="Nuevo Insumo" onClose={onClose} size="md">
      <div className="space-y-4">
        <Input
          label="Detalle"
          value={form.detalle}
          onChange={(e) => setForm({ ...form, detalle: e.target.value })}
          placeholder='Ej: BROCHA 4"'
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Categoría"
            value={form.categoria}
            onChange={(e) => setForm({ ...form, categoria: e.target.value as typeof form.categoria })}
            options={CATEGORIA_OPTIONS}
          />
          <Select
            label="Presentación"
            value={form.presentacion}
            onChange={(e) => setForm({ ...form, presentacion: e.target.value as typeof form.presentacion })}
            options={PRESENTACION_OPTIONS}
          />
        </div>
        <Input
          label="Precio unitario (sin IVA)"
          type="number"
          step="0.01"
          min="0.01"
          value={form.precioUnitario || ''}
          onChange={(e) => setForm({ ...form, precioUnitario: Number(e.target.value) || 0 })}
          placeholder="0.00"
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={createMutation.isPending}
            disabled={!form.detalle.trim() || form.precioUnitario <= 0}
          >
            Crear Insumo
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditInsumoModal({ insumo, onClose }: { insumo: InsumoDTO; onClose: () => void }) {
  const updateMutation = useUpdateInsumo();
  const deleteMutation = useDeleteInsumo();

  const [detalle, setDetalle] = useState(insumo.detalle);
  const [categoria, setCategoria] = useState(insumo.categoria);
  const [presentacion, setPresentacion] = useState(insumo.presentacion);
  const [precioUnitario, setPrecioUnitario] = useState(String(insumo.precioUnitario));

  const handleSubmit = async () => {
    if (!detalle.trim() || !Number(precioUnitario)) return;
    await updateMutation.mutateAsync({
      id: insumo.id,
      data: {
        detalle: detalle.trim(),
        categoria: categoria as typeof insumo.categoria,
        presentacion: presentacion as typeof insumo.presentacion,
        precioUnitario: Number(precioUnitario),
      },
    });
    onClose();
  };

  return (
    <Modal isOpen title="Editar Insumo" onClose={onClose} size="md">
      <div className="space-y-4">
        <Input
          label="Detalle"
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Categoría"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as typeof categoria)}
            options={CATEGORIA_OPTIONS}
          />
          <Select
            label="Presentación"
            value={presentacion}
            onChange={(e) => setPresentacion(e.target.value as typeof presentacion)}
            options={PRESENTACION_OPTIONS}
          />
        </div>
        <Input
          label="Precio unitario (sin IVA)"
          type="number"
          step="0.01"
          min="0.01"
          value={precioUnitario}
          onChange={(e) => setPrecioUnitario(e.target.value)}
          placeholder="0.00"
        />

        <div className="flex justify-between pt-2">
          <Button
            variant="ghost"
            className="text-red-500 hover:text-red-700"
            onClick={async () => {
              if (window.confirm('¿Desactivar este insumo? No aparecerá en futuras búsquedas.')) {
                await deleteMutation.mutateAsync(insumo.id);
                onClose();
              }
            }}
          >
            Desactivar
          </Button>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={updateMutation.isPending}
              disabled={!detalle.trim() || !Number(precioUnitario)}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
